/**
 * Allow Copy + — re-enables text selection / right-click / copy on locked pages
 *
 * Declares a single web-accessible-resource entry — `images/*` — matched to every
 * origin with no `use_dynamic_url`. Every bundled image is therefore fetchable from any
 * page at a stable URL.
 *
 * The vendor logo is the cleanest signal: `images/ainbar-logo.png` is uniquely named
 * and loads through `probeImage` with no CORS grant, so it survives a restrictive page
 * CSP. There are no non-image resources exposed, so the corroborators are other icons.
 *
 * Manifest verified against version 3.0.7.
 */

import { probeImage } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ajhbdcgfhlhhmocddefknjjkejcfpbnj';

export const allowCopy: ExtensionDefinition = {
  id: ID,
  name: 'Allow Copy +',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 700_000,
  notes:
    'Exposes images/* to every origin with no dynamic URL. The vendor-named ainbar-logo ' +
    'is conclusive and loads without a CORS grant.',
  signals: [
    {
      id: 'war-ainbar-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load images/ainbar-logo.png via <img> — a uniquely vendor-named image exposed ' +
        'to every origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'images/ainbar-logo.png'),
    },
    {
      id: 'war-icon-128-image',
      kind: 'web-accessible-resource',
      description:
        'Load images/128.png via <img> — the 128px toolbar icon, a second image path ' +
        'corroborating the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'images/128.png'),
    },
    {
      id: 'war-icon-16-on-image',
      kind: 'web-accessible-resource',
      description:
        'Load images/16-on.png via <img> — the active-state toolbar icon, a distinctive ' +
        'third path.',
      weight: 0.5,
      run: () => probeImage(ID, 'images/16-on.png'),
    },
  ],
};
